"use client";

import React, { useState, useEffect, useRef } from "react";
import Image from "next/image";
import Link from "next/link";
import { Section, Typography, Button } from "@/components/ui";
import { env } from "@/lib/environment";
import { NewButton } from "@/components/ui/NewButton";

interface AppButton {
  name: string;
  dashboardImage: string; // Each app has its own dashboard image
  icon?: string; // Icon/logo URL for the button
  description?: string; // Description for the app
  href?: string; // Optional link to app-specific page
}

interface HomegrownAppsSectionProps {
  title?: string;
  description?: string;
  apps?: AppButton[];
  dashboardImageAlt?: string;
  backgroundImage?: string;
  showButtons?: boolean; // Option to show/hide buttons
  className?: string;
  // Right side content props
  appName?: string;
  appDescription?: string;
  appIcon?: string;
  ctaText?: string;
  ctaHref?: string;
}

export function HomegrownAppsSection({
  title = "Display Data from Your Homegrown Applications",
  description = "Bring your internal dashboards, ERP systems, or custom-built portals to screens without re-engineering or exposing internal networks. Pickcel enables tailored integrations that match your security and data-integrity needs.",
  apps = [],
  dashboardImageAlt = "Dashboard Preview",
  backgroundImage,
  showButtons = true,
  className = "",
  appName,
  appDescription,
  appIcon,
  ctaText,
  ctaHref,
}: HomegrownAppsSectionProps) {
  // State to track the active app (default to first app)
  const [activeAppIndex, setActiveAppIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pauseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const currentDashboardImage = apps[activeAppIndex]?.dashboardImage || "";
  const currentApp = apps[activeAppIndex];
  const mountedTvImage = `${env.NEXT_PUBLIC_CDN_URL}/images/solutions/pickcel-dashboard/mounted-tv-dhashboard.z6WxgNLESRHE.webp`;

  // Autoplay functionality
  useEffect(() => {
    // Don't start autoplay if no apps or only one app
    if (apps.length === 0 || apps.length === 1) {
      return;
    }

    // Clear any existing intervals first
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // If paused or hovered, don't start/continue the interval
    if (isPaused || isHovered) {
      return;
    }

    // Set up interval to auto-advance every 4 seconds
    intervalRef.current = setInterval(() => {
      setActiveAppIndex((prevIndex) => {
        const nextIndex = (prevIndex + 1) % apps.length;
        return nextIndex;
      });
    }, 4000);

    // Cleanup on unmount or when dependencies change
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [apps.length, isPaused, isHovered]);

  // Handle manual button click - pause autoplay temporarily
  const handleAppClick = (index: number) => {
    // Clear any existing pause timeout
    if (pauseTimeoutRef.current) {
      clearTimeout(pauseTimeoutRef.current);
    }

    setActiveAppIndex(index);
    setIsPaused(true);

    // Resume autoplay after 1.5 seconds of inactivity
    pauseTimeoutRef.current = setTimeout(() => {
      setIsPaused(false);
      pauseTimeoutRef.current = null;
    }, 1000);
  };

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (pauseTimeoutRef.current) {
        clearTimeout(pauseTimeoutRef.current);
      }
    };
  }, []);

  return (
    <Section
      variant="default"
      background="white"
      className={className}
    >
      <div
        className="max-w-7xl container mx-auto"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        role="region"
        aria-label="Dashboard applications section"
      >
        {/* Title and Description */}
        <div className="text-center mb-8">
          <Typography variant="h2" as="h2" className="mb-6">
            {title}
          </Typography>
          <Typography variant="body" as="p" className="text-text-secondary max-w-4xl mx-auto leading-relaxed">
            {description}
          </Typography>
        </div>

        {/* Application Buttons - Clickable (Not Auto-scrolling) */}
        {showButtons && apps.length > 0 && (
          <div className="mb-12 max-w-5xl mx-auto">
            {/* Mobile View - Single Horizontal Scrollable Row */}
            <div className="flex overflow-x-auto gap-3 md:hidden scrollbar-hide pb-2">
              {apps.map((app, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => handleAppClick(index)}
                  className={`
                    flex-shrink-0 px-4 py-5 h-16 text-base text-center
                    font-medium bg-white rounded-full shadow-[2px_2px_10px_0px_rgba(0,0,0,0.10)]
                    flex items-center gap-3 justify-start min-w-[120px] md:min-w-[200px]
                    ${
                      activeAppIndex === index
                        ? "border border-blue-500 bg-white text-text-primary"
                        : "border border-gray-300 bg-white text-text-primary hover:border-gray-400"
                    }
                  `}
                >
                  {app.icon && (
                    <div className="flex-shrink-0 w-12 h-12 relative">
                      <Image
                        src={app.icon}
                        alt={`${app.name} icon`}
                        title={`${app.name} icon`}
                        width={24}
                        height={24}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <span className="text-left flex-1 truncate">{app.name}</span>
                </button>
              ))}
            </div>

            {/* Desktop View – 2 rows: top 4 buttons, bottom 5 buttons (same height/width styles) */}
            <div className="hidden md:flex flex-col gap-4 max-w-5xl mx-auto w-full">
              {/* Top row – 4 buttons (75% width, centered) */}
              <div className="flex w-3/4 mx-auto items-center justify-between gap-4">
                {apps.slice(0, 4).map((app, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleAppClick(index)}
                    className={`
                      flex-1 px-7 h-17 text-base text-left
                      font-medium bg-white rounded-full shadow-[2px_2px_10px_0px_rgba(0,0,0,0.10)]
                      flex items-center gap-3 justify-start min-w-0
                      ${
                        activeAppIndex === index
                          ? "border border-blue-500 text-text-primary"
                          : "border border-gray-300 text-text-secondary hover:border-gray-400"
                      }
                    `}
                  >
                    {app.icon && (
                      <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center">
                        <Image
                          src={app.icon}
                          alt={`${app.name} icon`}
                          title={`${app.name} icon`}
                          width={24}
                          height={24}
                          className="w-full h-full object-contain"
                        />
                      </div>
                    )}
                    <span className="flex-1 truncate">{app.name}</span>
                  </button>
                ))}
              </div>

              {/* Bottom row – 5 buttons (responsive for tablet) */}
              <div className="flex gap-2 md:gap-3 lg:gap-4 w-full max-w-full">
                {apps.slice(4, 9).map((app, index) => {
                  const isLastButton = index === 4; // Last button in the row
                  return (
                    <button
                      key={index + 4}
                      type="button"
                      onClick={() => handleAppClick(index + 4)}
                      className={`
                        ${isLastButton ? 'flex-[1.2]' : 'flex-1'} px-3 md:px-4 lg:px-7 h-17 text-sm md:text-base text-left
                        font-medium bg-white rounded-full shadow-[2px_2px_10px_0px_rgba(0,0,0,0.10)]
                        flex items-center gap-2 md:gap-3 justify-start min-w-0
                        ${
                          activeAppIndex === index + 4
                            ? "border border-blue-500 text-text-primary"
                            : "border border-gray-300 text-text-secondary hover:border-gray-400"
                        }
                      `}
                    >
                      {app.icon && (
                        <div className="flex-shrink-0 w-8 h-8 md:w-10 md:h-10 lg:w-12 lg:h-12 flex items-center justify-center">
                          <Image
                            src={app.icon}
                            alt={`${app.name} icon`}
                            title={`${app.name} icon`}
                            width={24}
                            height={24}
                            className="w-full h-full object-contain"
                          />
                        </div>
                      )}
                      <span className="flex-1 min-w-0 md:truncate lg:overflow-visible lg:whitespace-normal">{app.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Dashboard Display Section - Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 md:text-start text-center items-center">
          {/* Left Side - Mounted TV with Dashboard */}
          <div className="relative w-full">

            {/* Desktop: Mounted TV Frame with Dashboard Inside */}
            <div className="relative w-full">
              {/* Mounted TV Frame */}
              <Image
                src={mountedTvImage}
                alt="Mounted TV frame"
                title="Mounted TV frame"
                width={800}
                height={600}
                className="w-full h-auto"
                priority
              />

              {/* Dashboard Image Inside TV Screen */}
              {currentDashboardImage && (
                <div className="absolute top-[3%] left-[3%] right-[3%] bottom-[10%] overflow-hidden">
                  <Image
                    src={currentDashboardImage}
                    alt={`${currentApp?.name || ""} ${dashboardImageAlt}`}
                    title={`${currentApp?.name || ""} ${dashboardImageAlt}`}
                    width={800}
                    height={600}
                    className="w-full h-full object-cover transition-opacity duration-300"
                    priority
                  />
                </div>
              )}
            </div>
          </div>

          {/* Right Side - App Information */}
          <div className="flex flex-col justify-center space-y-6">
            {/* App Icon and Name - Dynamic based on selected app */}
            {currentApp?.icon && (
              <div className="flex items-center gap-4 md:justify-start justify-center">
                <div className="w-12 h-12 relative flex-shrink-0">
                  <Image
                    src={currentApp.icon}
                    alt={currentApp.name || "App icon"}
                    title={currentApp.name || "App icon"}
                    width={48}
                    height={48}
                    className="w-full h-full object-contain"
                  />
                </div>
                <Typography variant="h2" as="h2" className="text-text-primary text-2xl font-bold">
                  {currentApp.name || ""}
                </Typography>
              </div>
            )}

            {/* App Description - Dynamic based on selected app */}
            {(currentApp?.description || appDescription) && (
              <Typography variant="body" as="p" className="text-text-secondary text-base font-normal leading-relaxed">
                {currentApp?.description || appDescription}
              </Typography>
            )}

            {/* CTA Button */}
            {(currentApp?.href || ctaHref) && (
              <div>
                <Link
                  data-mixpanel-id={currentApp?.href ? "cta-know-more" : "cta-start-free-trial"}
                  href={currentApp?.href || ctaHref || "#"}
                > 
                  <NewButton variant="primary" arrow="grow" size="lg" asChild>
                    {currentApp?.href ? "Know more" : ctaText}
                  </NewButton>
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </Section>
  );
}

